import { DollarSign, MapPin, Phone, Mail, Clock, Shield, Users, CheckCircle, Star, Award, CreditCard, Zap, Building, FileText, Calculator, TrendingUp, BookOpen, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const CashAdvances = () => {
  const cashAdvanceFeatures = [
    {
      icon: <Zap className="w-8 h-8 text-orange-600" />,
      title: "Instant Approval",
      description: "Get approved within minutes with our automated system"
    },
    {
      icon: <Clock className="w-8 h-8 text-green-600" />,
      title: "24/7 Access",
      description: "Apply anytime, anywhere with our online platform"
    },
    {
      icon: <DollarSign className="w-8 h-8 text-blue-600" />,
      title: "Emergency Funds",
      description: "Quick access to cash for unexpected expenses"
    },
    {
      icon: <Shield className="w-8 h-8 text-purple-600" />,
      title: "Secure Process",
      description: "Bank-level security protects your personal information"
    }
  ];

  const emergencyUses = [
    "Medical emergencies and bills",
    "Car repairs and breakdowns",
    "Utility bills and rent",
    "Unexpected travel expenses",
    "Home repairs and maintenance",
    "School and education costs",
    "Pet emergencies and vet bills",
    "Family emergencies"
  ];

  const processSteps = [
    {
      step: "1",
      title: "Quick Application",
      description: "Complete our 2-minute online application form"
    },
    {
      step: "2",
      title: "Instant Decision",
      description: "Get approved instantly with our automated system"
    },
    {
      step: "3",
      title: "Fast Funding",
      description: "Receive funds in your account within hours"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img 
                src="https://quickloanpro.com/storage/2023/11/Payday-Loans-Louisiana-Logo.png" 
                alt="QuickLoanPro Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">QuickLoanPro</h1>
                <p className="text-gray-600">Your Trusted Financial Partner</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-gray-700">+1 (504) 438-1753</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-orange-600 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-orange-200 transition-colors">Home</Link>
            <Link to="/payday-loans" className="hover:text-orange-200 transition-colors">Payday Loans</Link>
            <Link to="/personal-loans" className="hover:text-orange-200 transition-colors">Personal Loans</Link>
            <Link to="/installment-loans" className="hover:text-orange-200 transition-colors">Installment Loans</Link>
            <Link to="/cash-advances" className="font-semibold">Cash Advances</Link>
            <Link to="/how-it-works" className="hover:text-orange-200 transition-colors">How It Works</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-red-700 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Cash Advances Louisiana
              </h2>
              <p className="text-xl md:text-2xl mb-8 opacity-90">
                Get instant cash advances when emergencies strike. Fast approval, immediate funding, 24/7 access from QuickLoanPro.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                  <Zap className="w-5 h-5 mr-2" />
                  Instant Approval
                </Badge>
                <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                  <Clock className="w-5 h-5 mr-2" />
                  24/7 Access
                </Badge>
                <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Emergency Funds
                </Badge>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl px-8 py-4"
                  onClick={() => window.open('https://quickloanpro.com/', '_blank')}
                >
                  Get Cash Advance
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-orange-600 font-bold text-xl px-8 py-4"
                >
                  Learn More
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              <img 
                src="/images/payday_loans_2.jpeg" 
                alt="QuickLoanPro Cash Advances Louisiana"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Cash Advance Features
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {cashAdvanceFeatures.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300 border-2 border-orange-200 bg-orange-50">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Get Your Cash Advance in 3 Steps
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Uses */}
      <section className="py-16 bg-gradient-to-r from-orange-50 to-red-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">
                When You Need Cash Advances
              </h2>
              <p className="text-gray-700 mb-6">
                Cash advances from QuickLoanPro are perfect for unexpected emergencies when you need money fast. Here are common situations where cash advances help:
              </p>
              <div className="grid grid-cols-1 gap-3">
                {emergencyUses.map((use, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-600" />
                    <span className="text-gray-700">{use}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="/images/payday_loans_1.png" 
                alt="Cash Advance Emergency Uses"
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Compare Our Financial Solutions
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Payday Loans</h3>
                <p className="text-sm text-gray-600 mb-4">Quick cash until payday</p>
                <Link to="/payday-loans">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <CreditCard className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Personal Loans</h3>
                <p className="text-sm text-gray-600 mb-4">Larger amounts, flexible terms</p>
                <Link to="/personal-loans">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <FileText className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Installment Loans</h3>
                <p className="text-sm text-gray-600 mb-4">Fixed monthly payments</p>
                <Link to="/installment-loans">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <HelpCircle className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">FAQ</h3>
                <p className="text-sm text-gray-600 mb-4">Get answers</p>
                <Link to="/faq">
                  <Button variant="outline" size="sm">View FAQ</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Need Cash Right Now?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Apply for your cash advance today. Instant approval and fast funding available 24/7.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl px-8 py-4"
              onClick={() => window.open('https://quickloanpro.com/', '_blank')}
            >
              Get Cash Advance Now
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-orange-600 font-bold text-xl px-8 py-4"
            >
              Call +1 (504) 438-1753
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">QuickLoanPro</h3>
              <p className="text-gray-400 mb-4">
                Your trusted partner for cash advances and emergency financial assistance across Louisiana.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 (504) 438-1753</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@quickloanpro.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>4901 Bloomfield St, New Orleans, LA 70121</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Loan Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/payday-loans" className="hover:text-white">Payday Loans</Link></li>
                <li><Link to="/personal-loans" className="hover:text-white">Personal Loans</Link></li>
                <li><Link to="/installment-loans" className="hover:text-white">Installment Loans</Link></li>
                <li><Link to="/cash-advances" className="hover:text-white">Cash Advances</Link></li>
                <li><Link to="/title-loans" className="hover:text-white">Title Loans</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                <li><Link to="/responsible-lending" className="hover:text-white">Responsible Lending</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 QuickLoanPro. All rights reserved.</p>
            <p className="mt-2">Licensed financial service provider committed to responsible lending practices.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CashAdvances;